import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Terminal, LogOut, Search, Globe, CreditCard, Server, ShieldCheck,
  Download, Trash2, History, ChevronRight, AlertTriangle, Zap, MousePointerClick,
  Code2, Copy, Play, Network, FileStack, FileSpreadsheet, Eye, LogIn,
} from "lucide-react";
import { matrixApi, matrixAuth, matrixError } from "@/lib/matrixApi";
import { CircuitBg } from "@/components/CircuitBg";
import { MatrixProxies } from "@/components/MatrixProxies";
import { MatrixWatch } from "@/components/MatrixWatch";
import { MatrixLogin as MatrixSiteLogin } from "@/components/MatrixLogin";

const COST = 250;
const CLICK_LIMITS = [10, 25, 40, 60];
const PAGE_LIMITS = [1, 3, 5, 10, 15];

/* ----------------------------- Lluvia roja ----------------------------- */
const MatrixRain = () => {
  const ref = React.useRef(null);
  useEffect(() => {
    const c = ref.current, ctx = c.getContext("2d");
    let raf, w, h, cols, drops;
    const chars = "01ｱｲｳｴｵｶｷｸ日二三四五ABCDEF<>/{}=+アイウ";
    const resize = () => {
      w = c.width = c.offsetWidth; h = c.height = c.offsetHeight;
      cols = Math.floor(w / 14); drops = Array(cols).fill(1);
    };
    resize();
    const draw = () => {
      ctx.fillStyle = "rgba(5,6,10,0.08)"; ctx.fillRect(0, 0, w, h);
      ctx.font = "14px monospace";
      for (let i = 0; i < drops.length; i++) {
        const t = chars[Math.floor(Math.random() * chars.length)];
        ctx.fillStyle = Math.random() > 0.97 ? "#ffb169" : "#e0352a";
        ctx.fillText(t, i * 14, drops[i] * 14);
        if (drops[i] * 14 > h && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      }
      raf = requestAnimationFrame(() => setTimeout(draw, 55));
    };
    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full opacity-[0.22] pointer-events-none" />;
};

/* ------------------------------- Login --------------------------------- */
const MatrixLogin = ({ onIn }) => {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await matrixApi.post("/auth/login", { username: u, password: p });
      matrixAuth.save(data.token);
      onIn(data.username);
    } catch (err) { toast.error(matrixError(err, "No se pudo entrar")); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen relative flex items-center justify-center px-6 theme-noir"
      style={{ backgroundColor: "#05060a" }} data-testid="matrix-login">
      <CircuitBg />
      <MatrixRain />
      <form onSubmit={submit} className="glass-card forge-card p-8 w-full max-w-sm relative z-10">
        <div className="flex flex-col items-center mb-6">
          <Terminal className="w-10 h-10 text-red-500" style={{ filter: "drop-shadow(0 0 12px #e0352a)" }} />
          <h1 className="font-heading text-2xl mt-3 text-white tracking-[0.2em]">MATRIX</h1>
          <p className="text-[11px] text-white/45 mt-1 tracking-[0.3em]">ÁREA RESTRINGIDA</p>
        </div>
        <input data-testid="matrix-user-input" value={u} onChange={(e) => setU(e.target.value)}
          placeholder="Usuario" autoComplete="username"
          className="w-full glass-card px-4 py-3 mb-3 bg-black/40 text-white text-sm outline-none" />
        <input data-testid="matrix-pass-input" type="password" value={p}
          onChange={(e) => setP(e.target.value)} placeholder="Contraseña" autoComplete="current-password"
          className="w-full glass-card px-4 py-3 mb-5 bg-black/40 text-white text-sm outline-none" />
        <button data-testid="matrix-login-btn" disabled={busy}
          className="matrix-btn w-full py-3 rounded-xl font-heading tracking-wider flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} ENTRAR
        </button>
        <p className="text-[11px] text-white/35 text-center mt-4">Pase de 7 días · sin bloqueo por intentos</p>
      </form>
    </div>
  );
};

/* --------------------------- Panel principal --------------------------- */
const Stat = ({ label, value, accent }) => (
  <div className="glass-card p-3 text-center">
    <div className={`text-xl font-bold ${accent || "text-white"}`}>{value}</div>
    <div className="text-[10px] text-white/45 uppercase tracking-wider mt-0.5">{label}</div>
  </div>
);

const MatrixTool = ({ username, onOut }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [tab, setTab] = useState("apis");
  const [q, setQ] = useState("");
  const [hist, setHist] = useState([]);
  const [clickOn, setClickOn] = useState(false);
  const [maxClicks, setMaxClicks] = useState(25);
  const [reqs, setReqs] = useState([]);
  const [tester, setTester] = useState(null);
  const [maxPages, setMaxPages] = useState(1);
  const [useProxy, setUseProxy] = useState(false);
  const [showProxies, setShowProxies] = useState(false);
  const [proxyCount, setProxyCount] = useState(null);
  const [showWatch, setShowWatch] = useState(false);
  const [watchCount, setWatchCount] = useState(null);
  const [watchUrl, setWatchUrl] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [sessionId, setSessionIdRaw] = useState(() => localStorage.getItem("matrix_site_session") || "");
  const setSessionId = (id) => {
    setSessionIdRaw(id);
    if (id) localStorage.setItem("matrix_site_session", id);
    else localStorage.removeItem("matrix_site_session");
  };
  const [sessions, setSessions] = useState([]);

  const addWatch = (u) => { setWatchUrl(u); setShowWatch(true); };

  useEffect(() => {
    matrixApi.get("/proxies")
      .then(({ data }) => setProxyCount(data.proxies.filter((p) => p.enabled).length))
      .catch(() => {});
    matrixApi.get("/watch")
      .then(({ data }) => setWatchCount(data.watches.filter((w) => w.enabled).length))
      .catch(() => {});
    matrixApi.get("/sessions").then(({ data }) => {
      setSessions(data.sessions);
      const saved = localStorage.getItem("matrix_site_session");
      if (saved && !data.sessions.some((s) => s.session_id === saved)) setSessionId("");
    }).catch(() => {});
  }, []);

  const loadHist = () => matrixApi.get("/history").then(({ data }) => setHist(data.jobs)).catch(() => {});
  useEffect(() => { loadHist(); }, []);

  const run = async () => {
    if (!url.trim()) return;
    setBusy(true); setRes(null); setReqs([]);
    try {
      const { data } = await matrixApi.post("/analyze",
        { url, click_enabled: clickOn, max_clicks: maxClicks,
          max_pages: maxPages, use_proxy: useProxy, session_id: sessionId },
        { timeout: 600000 });
      setRes(data); setTab("apis"); loadHist();
      matrixApi.get(`/job/${data.job_id}/requests`)
        .then(({ data: d }) => setReqs(d.requests)).catch(() => {});
      toast.success(`Análisis listo · ${COST} créditos`);
    } catch (e) { toast.error(matrixError(e, "No se pudo analizar")); }
    finally { setBusy(false); }
  };

  const openJob = async (id) => {
    try {
      const { data } = await matrixApi.get(`/job/${id}`);
      setRes(data); setTab("apis");
      const { data: d } = await matrixApi.get(`/job/${id}/requests`);
      setReqs(d.requests);
    } catch (e) { toast.error(matrixError(e)); }
  };
  const delJob = async (id) => {
    await matrixApi.delete(`/job/${id}`); loadHist();
    if (res?.job_id === id) setRes(null);
  };
  const downloadTxt = (id) =>
    downloadFile(`/job/${id}/txt`, `matrix_${id.slice(0, 8)}.txt`);

  const copyCurl = async (i) => {
    try {
      const { data } = await matrixApi.get(`/job/${res.job_id}/curl?i=${i}`);
      await navigator.clipboard.writeText(data.curl);
      toast.success("cURL copiado");
    } catch (e) { toast.error(matrixError(e)); }
  };

  const downloadPostman = (id) =>
    downloadFile(`/job/${id}/postman`, `matrix_${id.slice(0, 8)}_postman.json`);

  const downloadFile = (path, filename) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/matrix${path}`,
      { headers: { Authorization: `Bearer ${matrixAuth.token()}` } })
      .then((r) => {
        if (!r.ok) throw new Error("No se pudo bajar el archivo");
        return r.blob();
      })
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b); a.download = filename; a.click();
      })
      .catch((e) => toast.error(e.message));
  };

  const sm = res?.api_map?.summary;
  const servers = res?.api_map?.servers || [];
  const filtered = servers.map((s) => ({
    ...s,
    endpoints: s.endpoints.filter((e) =>
      !q || `${e.method} ${e.path}`.toLowerCase().includes(q.toLowerCase())),
  })).filter((s) => s.endpoints.length);

  return (
    <div className="min-h-screen relative theme-noir" style={{ backgroundColor: "#05060a" }}
      data-testid="matrix-tool">
      <MatrixRain />
      <div className="relative z-10 max-w-5xl mx-auto px-5 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Terminal className="w-6 h-6 text-red-500" style={{ filter: "drop-shadow(0 0 10px #e0352a)" }} />
            <span className="font-heading text-xl text-white tracking-[0.2em]">MATRIX</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/60" data-testid="matrix-username">{username}</span>
            <button data-testid="matrix-logout-btn" onClick={onOut}
              className="glass-btn px-3 py-1.5 rounded-xl text-xs flex items-center gap-1.5 text-red-300">
              <LogOut className="w-3.5 h-3.5" /> Salir
            </button>
          </div>
        </div>

        <div className="glass-card forge-card p-4 flex flex-col sm:flex-row gap-2">
          <div className="flex-1 flex items-center gap-2 glass-card px-3 bg-black/40">
            <Globe className="w-4 h-4 text-red-400 shrink-0" />
            <input data-testid="matrix-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()}
              placeholder="ejemplo.com" className="flex-1 bg-transparent py-3 text-white text-sm outline-none" />
          </div>
          <button data-testid="matrix-analyze-btn" onClick={run} disabled={busy}
            className="matrix-btn px-6 py-3 rounded-xl font-heading tracking-wider flex items-center justify-center gap-2 shrink-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {busy ? "ANALIZANDO…" : `ANALIZAR · ${COST} cr`}
          </button>
        </div>

        <div className="glass-card p-3 mt-2 flex flex-wrap items-center gap-3">
          <button data-testid="matrix-click-toggle" onClick={() => setClickOn((v) => !v)}
            className="flex items-center gap-2 text-xs text-white/80">
            <span className={`w-10 h-6 rounded-full relative transition-colors ${clickOn ? "bg-red-600/80" : "bg-white/15"}`}>
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${clickOn ? "left-5" : "left-1"}`} />
            </span>
            Hacer clic en botones y menús
          </button>
          {clickOn && (
            <div className="flex items-center gap-1.5">
              {CLICK_LIMITS.map((n) => (
                <button key={n} data-testid={`matrix-clicks-${n}`} onClick={() => setMaxClicks(n)}
                  className={`matrix-chip ${maxClicks === n ? "is-active" : ""} px-3 py-1 rounded-lg text-[11px]`}>
                  {n}
                </button>
              ))}
              <span className="text-[11px] text-white/40 ml-1">clics máx.</span>
            </div>
          )}
          <span className="text-[11px] text-white/35">
            Nunca pulsa Pagar, Comprar, Salir ni Borrar
          </span>
        </div>

        <div className="glass-card p-3 mt-2 flex flex-wrap items-center gap-3">
          <span className="text-xs text-white/80">Recorrer el sitio</span>
          <div className="flex items-center gap-1.5">
            {PAGE_LIMITS.map((n) => (
              <button key={n} data-testid={`matrix-pages-${n}`} onClick={() => setMaxPages(n)}
                className={`matrix-chip ${maxPages === n ? "is-active" : ""} px-3 py-1 rounded-lg text-[11px]`}>
                {n}
              </button>
            ))}
            <span className="text-[11px] text-white/40 ml-1">
              {maxPages === 1 ? "solo esta página" : "páginas del mismo sitio"}
            </span>
          </div>
          <div className="flex-1" />
          <button data-testid="matrix-proxy-toggle" onClick={() => setUseProxy((v) => !v)}
            className="flex items-center gap-2 text-xs text-white/80">
            <span className={`w-10 h-6 rounded-full relative transition-colors ${useProxy ? "bg-red-600/80" : "bg-white/15"}`}>
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${useProxy ? "left-5" : "left-1"}`} />
            </span>
            Salir por mis proxies
          </button>
          <button data-testid="matrix-login-btn" onClick={() => setShowLogin(true)}
            className={`glass-btn px-3 py-1.5 rounded-xl text-xs flex items-center gap-1.5 ${
              sessionId ? "text-emerald-300" : "text-red-300"}`}>
            <LogIn className="w-3.5 h-3.5" />
            {sessionId
              ? `Conectado: ${(sessions.find((s) => s.session_id === sessionId) || {}).host || "sesión"}`
              : "Entrar al sitio"}
          </button>
          {sessionId && (
            <button data-testid="matrix-session-clear" onClick={() => setSessionId("")}
              className="glass-btn px-2 py-1.5 rounded-xl text-[10px] text-white/50">
              Desconectar
            </button>
          )}
          <button data-testid="matrix-watch-btn" onClick={() => { setWatchUrl(""); setShowWatch(true); }}
            className="glass-btn px-3 py-1.5 rounded-xl text-xs flex items-center gap-1.5 text-red-300">
            <Eye className="w-3.5 h-3.5" /> Vigiladas
            {watchCount !== null && <span className="text-white/40">{watchCount}</span>}
          </button>
          <button data-testid="matrix-proxies-btn" onClick={() => setShowProxies(true)}
            className="glass-btn px-3 py-1.5 rounded-xl text-xs flex items-center gap-1.5 text-red-300">
            <Network className="w-3.5 h-3.5" /> Mis proxies
            {proxyCount !== null && <span className="text-white/40">{proxyCount}</span>}
          </button>
        </div>

        {showProxies && (
          <MatrixProxies onClose={() => setShowProxies(false)} onCount={setProxyCount} />
        )}
        {showWatch && (
          <MatrixWatch onClose={() => setShowWatch(false)} onCount={setWatchCount}
            defaultUrl={watchUrl} />
        )}
        {showLogin && (
          <MatrixSiteLogin onClose={() => setShowLogin(false)} activeSession={sessionId}
            onCount={() => matrixApi.get("/sessions")
              .then(({ data }) => setSessions(data.sessions)).catch(() => {})}
            onUse={(id) => {
              setSessionId(id);
              matrixApi.get("/sessions")
                .then(({ data }) => setSessions(data.sessions)).catch(() => {});
            }} />
        )}

        {busy && (
          <p className="text-xs text-white/50 mt-3 animate-pulse">
            Abriendo {maxPages > 1 ? `hasta ${maxPages} páginas` : "la página"} en un navegador real…{" "}
            {clickOn ? `con hasta ${maxClicks} clics por página, ` : ""}
            {useProxy ? "saliendo por tus proxies, " : ""}puede tardar
            {maxPages > 1 ? " varios minutos." : " 20–60 s."}
          </p>
        )}

        {res && (
          <div className="mt-6" data-testid="matrix-result">
            {res.warning && (
              <div className="glass-card p-3 mb-4 flex items-start gap-2 text-xs text-amber-200 border border-amber-400/30">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {res.warning}
              </div>
            )}
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="text-sm text-white/70 truncate">
                <span className="text-red-400">{res.mode}</span> · {res.url}
                {res.proxy && <span className="text-white/40"> · proxy {res.proxy}</span>}
                {res.logged_in && <span className="text-emerald-400"> · con sesión iniciada</span>}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <button data-testid="matrix-download-btn" onClick={() => downloadTxt(res.job_id)}
                  className="matrix-btn px-4 py-2 rounded-xl text-xs flex items-center gap-1.5">
                  <Download className="w-3.5 h-3.5" /> TXT
                </button>
                <button data-testid="matrix-csv-apis-btn"
                  onClick={() => downloadFile(`/job/${res.job_id}/csv/apis`,
                    `matrix_${res.job_id.slice(0, 8)}_apis.csv`)}
                  className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> CSV APIs
                </button>
                <button data-testid="matrix-csv-buttons-btn"
                  onClick={() => downloadFile(`/job/${res.job_id}/csv/botones`,
                    `matrix_${res.job_id.slice(0, 8)}_botones.csv`)}
                  className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> CSV Botones
                </button>
                <button data-testid="matrix-xlsx-btn"
                  onClick={() => downloadFile(`/job/${res.job_id}/xlsx`,
                    `matrix_${res.job_id.slice(0, 8)}.xlsx`)}
                  className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5 text-emerald-300">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                </button>
                <button data-testid="matrix-watch-add-btn"
                  onClick={() => addWatch(res.url)}
                  className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5 text-red-300">
                  <Eye className="w-3.5 h-3.5" /> Vigilar esta web
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5">
              <Stat label="Llamadas" value={sm?.total_calls ?? 0} />
              <Stat label="Endpoints" value={sm?.endpoints ?? 0} accent="text-red-400" />
              <Stat label="Servidores" value={sm?.servers ?? 0} />
              <Stat label="Terceros" value={sm?.third_party_servers ?? 0} />
              <Stat label="Errores" value={sm?.errors ?? 0} accent="text-amber-300" />
              <Stat label="Red total" value={res.network_count} />
            </div>

            {res.pages?.length > 1 && (
              <div className="glass-card p-4 mb-5" data-testid="matrix-pages">
                <div className="text-xs text-white font-semibold mb-2 flex items-center gap-2">
                  <FileStack className="w-3.5 h-3.5 text-red-400" />
                  Páginas recorridas: {res.pages.length}
                </div>
                {res.pages.map((p, i) => (
                  <div key={i} className="text-[11px] text-white/60 font-mono truncate">
                    {i + 1}. {p.url} <span className="text-white/35">
                      · {p.buttons} botones · {p.calls} llamadas</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
              {[["apis", "Mapa de APIs", Server], ["botones", "Botones", MousePointerClick],
                ["red", "Peticiones", Code2], ["pagos", "Pagos", CreditCard]].map(([k, l, Ic]) => (
                <button key={k} data-testid={`matrix-tab-${k}`} onClick={() => setTab(k)}
                  className={`matrix-chip ${tab === k ? "is-active" : ""} px-4 py-2 rounded-xl text-xs flex items-center gap-1.5 shrink-0 whitespace-nowrap`}>
                  <Ic className="w-3.5 h-3.5" /> {l}
                  {k === "pagos" && res.payments.has_payments && (
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" />)}
                  {k === "botones" && <span className="text-white/40">{res.buttons?.length || 0}</span>}
                  {k === "red" && <span className="text-white/40">{reqs.length}</span>}
                </button>
              ))}
            </div>

            {tab === "apis" && (
              <>
                <div className="flex items-center gap-2 glass-card px-3 mb-3 bg-black/30">
                  <Search className="w-4 h-4 text-white/40" />
                  <input data-testid="matrix-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar endpoint…" className="flex-1 bg-transparent py-2.5 text-white text-sm outline-none" />
                </div>
                {sm?.auth?.length > 0 && (
                  <div className="text-xs text-white/55 mb-3 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-red-400" /> Autenticación: {sm.auth.join(", ")}
                  </div>
                )}
                {filtered.length === 0 && <p className="text-sm text-white/45">Sin endpoints de datos.</p>}
                {filtered.map((s) => (
                  <div key={s.host} className="glass-card p-4 mb-3" data-testid={`matrix-server-${s.host}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.third_party ? "bg-white/10 text-white/60" : "bg-red-500/20 text-red-300"}`}>
                        {s.third_party ? "TERCERO" : "PROPIO"}
                      </span>
                      <span className="text-sm text-white font-mono truncate">{s.host}</span>
                    </div>
                    <div className="space-y-1.5">
                      {s.endpoints.map((e, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-lg hover:bg-white/[0.03]">
                          <span className={`font-mono font-bold w-14 shrink-0 ${
                            e.method === "GET" ? "text-emerald-400" : "text-amber-300"}`}>{e.method}</span>
                          <span className="font-mono text-white/80 truncate flex-1">{e.path}</span>
                          {e.is_payment && <span className="text-[10px] text-red-300 shrink-0">💳 PAGO</span>}
                          <span className="text-white/40 shrink-0">{e.calls}×</span>
                          <span className="text-white/35 shrink-0 font-mono">{(e.statuses || []).join(",")}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}

            {tab === "botones" && (
              <div data-testid="matrix-buttons">
                {res.explore && (
                  <div className="glass-card p-4 mb-3 text-xs">
                    <div className="text-white font-semibold mb-2">
                      Clics realizados: {res.explore.clicks_done}
                    </div>
                    {res.explore.clicked?.map((c, i) => (
                      <div key={i} className="text-white/65 truncate">
                        ✔ Clic {c.click_no}: {c.text} → <span className="text-red-300">{c.new_calls} llamadas nuevas</span>
                      </div>
                    ))}
                    {res.explore.skipped?.slice(0, 8).map((s, i) => (
                      <div key={`s${i}`} className="text-white/35 truncate">✖ {s.text} ({s.reason})</div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 glass-card px-3 mb-3 bg-black/30">
                  <Search className="w-4 h-4 text-white/40" />
                  <input data-testid="matrix-buttons-search" value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar botón…" className="flex-1 bg-transparent py-2.5 text-white text-sm outline-none" />
                </div>
                <div className="glass-card divide-y divide-white/5">
                  {(res.buttons || [])
                    .filter((b) => !q || `${b.text} ${b.kind} ${b.id}`.toLowerCase().includes(q.toLowerCase()))
                    .slice(0, 300).map((b, i) => (
                      <div key={i} className="px-4 py-2.5 flex items-center gap-3 text-xs"
                        data-testid={`matrix-button-${i}`}>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/8 text-white/55 shrink-0 w-28 text-center">
                          {b.kind}
                        </span>
                        <span className="text-white/85 truncate flex-1">{b.text || "(sin texto)"}</span>
                        {b.href && <span className="text-white/35 font-mono truncate max-w-[30%]">{b.href}</span>}
                        {b.disabled && <span className="text-amber-300 shrink-0">off</span>}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {tab === "red" && (
              <div data-testid="matrix-requests">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2 glass-card px-3 flex-1 bg-black/30">
                    <Search className="w-4 h-4 text-white/40" />
                    <input data-testid="matrix-req-search" value={q} onChange={(e) => setQ(e.target.value)}
                      placeholder="Buscar petición…" className="flex-1 bg-transparent py-2.5 text-white text-sm outline-none" />
                  </div>
                  <button data-testid="matrix-postman-btn" onClick={() => downloadPostman(res.job_id)}
                    className="matrix-btn px-4 py-2 rounded-xl text-xs shrink-0">Postman</button>
                </div>
                <div className="glass-card divide-y divide-white/5">
                  {reqs.filter((r) => !q || r.url.toLowerCase().includes(q.toLowerCase()))
                    .slice(0, 200).map((r) => (
                      <div key={r.i} className="px-3 py-2 flex items-center gap-2 text-xs"
                        data-testid={`matrix-req-${r.i}`}>
                        <span className={`font-mono font-bold w-12 shrink-0 ${
                          r.method === "GET" ? "text-emerald-400" : "text-amber-300"}`}>{r.method}</span>
                        <span className="text-white/75 truncate flex-1 font-mono">{r.url}</span>
                        {r.after_click && (
                          <span className="text-[10px] text-red-300 shrink-0" title={r.triggered_by}>
                            clic {r.after_click}
                          </span>
                        )}
                        <span className="text-white/35 shrink-0 font-mono">{r.status ?? "—"}</span>
                        <button onClick={() => copyCurl(r.i)} title="Copiar como cURL"
                          data-testid={`matrix-curl-${r.i}`} className="text-white/50 hover:text-red-300 shrink-0">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button title="Probar esta petición" data-testid={`matrix-test-${r.i}`}
                          onClick={() => setTester({ method: r.method, url: r.url, headers: "{}", body: r.post_data || "", resp: null, busy: false })}
                          className="text-white/50 hover:text-red-300 shrink-0">
                          <Play className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {tab === "pagos" && (
              <div data-testid="matrix-payments">
                {!res.payments.has_payments && (
                  <p className="text-sm text-white/45">No se detectaron pasarelas ni endpoints de pago.</p>
                )}
                {res.payments.providers.map((p) => (
                  <div key={p.provider} className="glass-card p-4 mb-3" data-testid={`matrix-pay-${p.provider}`}>
                    <div className="flex items-center gap-2 text-white font-semibold">
                      <CreditCard className="w-4 h-4 text-red-400" /> {p.provider}
                      <span className="text-xs text-white/45">({p.calls.length} llamadas)</span>
                    </div>
                    {p.calls.slice(0, 6).map((c, i) => (
                      <div key={i} className="text-[11px] text-white/55 font-mono mt-1.5 truncate">
                        {c.method} {c.url} [{c.status ?? "—"}]
                      </div>
                    ))}
                  </div>
                ))}
                {res.payments.self_endpoints?.length > 0 && (
                  <div className="glass-card p-4">
                    <div className="text-white font-semibold text-sm mb-2">Endpoints de pago del propio sitio</div>
                    {res.payments.self_endpoints.slice(0, 15).map((c, i) => (
                      <div key={i} className="text-[11px] text-white/55 font-mono mt-1 truncate">
                        {c.method} {c.url} [{c.status ?? "—"}]
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Probador de peticiones */}
        {tester && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-5"
            data-testid="matrix-tester" onClick={(e) => e.target === e.currentTarget && setTester(null)}>
            <div className="glass-card forge-card p-5 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <span className="font-heading text-white tracking-wider">PROBAR PETICIÓN</span>
                <button onClick={() => setTester(null)} data-testid="matrix-tester-close"
                  className="text-white/50 hover:text-white">✕</button>
              </div>
              <div className="flex gap-2 mb-2">
                <select value={tester.method} data-testid="matrix-tester-method"
                  onChange={(e) => setTester({ ...tester, method: e.target.value })}
                  className="glass-card px-3 py-2 bg-black/50 text-white text-xs outline-none">
                  {["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"].map((m) =>
                    <option key={m} value={m} className="bg-black">{m}</option>)}
                </select>
                <input value={tester.url} data-testid="matrix-tester-url"
                  onChange={(e) => setTester({ ...tester, url: e.target.value })}
                  className="flex-1 glass-card px-3 py-2 bg-black/50 text-white text-xs font-mono outline-none" />
              </div>
              <textarea value={tester.headers} data-testid="matrix-tester-headers" rows={3}
                onChange={(e) => setTester({ ...tester, headers: e.target.value })}
                placeholder='Headers en JSON, ej. {"Authorization": "Bearer ..."}'
                className="w-full glass-card px-3 py-2 mb-2 bg-black/50 text-white text-xs font-mono outline-none" />
              <textarea value={tester.body} data-testid="matrix-tester-body" rows={4}
                onChange={(e) => setTester({ ...tester, body: e.target.value })}
                placeholder="Cuerpo (JSON, formulario, etc.)"
                className="w-full glass-card px-3 py-2 mb-3 bg-black/50 text-white text-xs font-mono outline-none" />
              <button data-testid="matrix-tester-send" disabled={tester.busy}
                onClick={async () => {
                  setTester((t) => ({ ...t, busy: true }));
                  let headers = {};
                  try { headers = JSON.parse(tester.headers || "{}"); }
                  catch { toast.error("Los headers no son JSON válido"); setTester((t) => ({ ...t, busy: false })); return; }
                  try {
                    const { data } = await matrixApi.post("/test-request", {
                      method: tester.method, url: tester.url, headers,
                      body: tester.body || null,
                    });
                    setTester((t) => ({ ...t, resp: data, busy: false }));
                  } catch (e) {
                    toast.error(matrixError(e, "No se pudo enviar"));
                    setTester((t) => ({ ...t, busy: false }));
                  }
                }}
                className="matrix-btn px-5 py-2.5 rounded-xl text-xs flex items-center gap-2">
                {tester.busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                Enviar
              </button>
              {tester.resp && (
                <div className="mt-4" data-testid="matrix-tester-response">
                  <div className="text-xs text-white/70 mb-2">
                    Estado <span className={tester.resp.status < 400 ? "text-emerald-400" : "text-rose-400"}>
                      {tester.resp.status}</span> · {tester.resp.elapsed_ms} ms · {tester.resp.content_type}
                  </div>
                  <pre className="glass-card p-3 bg-black/60 text-[11px] text-white/70 overflow-x-auto max-h-64">
                    {tester.resp.body?.slice(0, 4000) || "(sin cuerpo)"}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Historial */}
        <div className="mt-8">
          <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
            <History className="w-4 h-4 text-red-400" /> Historial
          </div>
          {hist.length === 0 && <p className="text-xs text-white/40">Aún no hay análisis.</p>}
          <div className="space-y-2">
            {hist.map((j) => (
              <div key={j.job_id} className="glass-card px-4 py-2.5 flex items-center gap-3 text-xs"
                data-testid={`matrix-hist-${j.job_id}`}>
                <button onClick={() => openJob(j.job_id)} className="flex-1 min-w-0 text-left flex items-center gap-2">
                  <ChevronRight className="w-3.5 h-3.5 text-red-400 shrink-0" />
                  <span className="truncate text-white/80">{j.url}</span>
                  <span className="text-white/35 shrink-0">{j.created_at?.slice(0, 16).replace("T", " ")}</span>
                </button>
                <button onClick={() => downloadTxt(j.job_id)} className="text-white/50 hover:text-red-300">
                  <Download className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => delJob(j.job_id)} className="text-white/50 hover:text-red-400"
                  data-testid={`matrix-hist-del-${j.job_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------ Contenedor ----------------------------- */
export default function Matrix() {
  const [state, setState] = useState("checking");
  const [username, setUsername] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    if (!matrixAuth.token()) return setState("guest");
    matrixApi.get("/auth/me")
      .then(({ data }) => { setUsername(data.username); setState("in"); })
      .catch(() => { matrixAuth.clear(); setState("guest"); });
  }, []);

  if (state === "checking")
    return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#05060a" }}>
      <Loader2 className="w-6 h-6 animate-spin text-red-500" /></div>;
  if (state === "guest")
    return <MatrixLogin onIn={(u) => { setUsername(u); setState("in"); }} />;
  return <MatrixTool username={username} onOut={() => { matrixAuth.clear(); setState("guest"); }} />;
}
